import { useState, useEffect } from "react";
import { useMsal, useAccount, AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import AFRPopSubmissionForm from "./components/AFRPopSubmissionForm";
import DashboardScreen from "./components/DashboardScreen";
import LoginPage from "./components/LoginPage";
import { TRANSLATIONS } from "./constants/translations";
import { API_ENDPOINTS } from "./constants/api";
import { loginRequest } from "./authConfig";

import "./styles/maersk-theme.css";
import "./styles/afr-pop-form.css";

function App() {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  const getToken = async () => {
    if (!accounts[0]) return null;
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return response.idToken;
    } catch {
      return null;
    }
  };

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  const [userPhotoUrl, setUserPhotoUrl] = useState(null);

  useEffect(() => {
    if (!accounts[0]) return;
    let objectUrl = null;
    (async () => {
      try {
        const resp = await instance.acquireTokenSilent({
          scopes: ["User.Read"],
          account: accounts[0],
        });
        const photoResp = await fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
          headers: { Authorization: `Bearer ${resp.accessToken}` },
        });
        if (photoResp.ok) {
          const blob = await photoResp.blob();
          objectUrl = URL.createObjectURL(blob);
          setUserPhotoUrl(objectUrl);
        }
      } catch {
        // no photo available — fallback to initials
      }
    })();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [accounts[0]?.homeAccountId]);

  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState("");
  const [toolResult, setToolResult] = useState(null);
  const [toolResultsByFile, setToolResultsByFile] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editedResult, setEditedResult] = useState("");
  const [editedResultsByFile, setEditedResultsByFile] = useState({});
  const [, setCurrentFileName] = useState("");
  const [currentFileKey, setCurrentFileKey] = useState("");
  const [mismatchEmail, setMismatchEmail] = useState("");
  const [language, setLanguage] = useState("en");
  const [showMismatchPrompt, setShowMismatchPrompt] = useState(false);
  const [mismatchComment, setMismatchComment] = useState("");
  const [showEditFilePicker, setShowEditFilePicker] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [pendingConfirmData, setPendingConfirmData] = useState([]);
  const [isConfirming, setIsConfirming] = useState(false);
  const [caseIdsByFile, setCaseIdsByFile] = useState({});     // fileKey → caseId
  const [verifyingByFile, setVerifyingByFile] = useState({}); // fileKey → bool
  const [verificationResults, setVerificationResults] = useState({});
  const [s4LookupByFile, setS4LookupByFile] = useState({});   // fileKey → first verifyCase result (for invoice populate)
  const [s4InvoiceByFile, setS4InvoiceByFile] = useState({}); // fileKey → S4 invoice number string
  const [currentScreen, setCurrentScreen] = useState("form");
  const [submissionHistory, setSubmissionHistory] = useState([]);

  const VERIFY_ON_CONFIRM = true;

  const strings = TRANSLATIONS[language] || TRANSLATIONS.en;

  const normalizeValue = (value) => {
    if (value == null) return "";
    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch (_e) {
        return String(value);
      }
    }
    return String(value);
  };

  const getFileKey = (file) => {
    if (!file) return "";
    return `${file.name}-${file.size}-${file.lastModified}`;
  };

  const handleFileUploaded = async (file, options = {}) => {
    if (!file) return;
    const { prefetch = false, setCurrent = true, extractIfMissing = false } = options;
    const fileKey = getFileKey(file);
    if (setCurrent) {
      setCurrentFileName(file.name || "");
      setCurrentFileKey(fileKey);
    }

    const cachedResult = toolResultsByFile[fileKey];
    if (cachedResult) {
      if (setCurrent) {
        setToolResult(cachedResult);
        setEditedResult(editedResultsByFile[fileKey] || "");
        setStatus(null);
        setMessage("");
      }
      return cachedResult;
    }

    // Not in cache — only extract if explicitly requested
    if (!extractIfMissing && !prefetch) {
      if (setCurrent) {
        setToolResult(null);
        setEditedResult("");
        setStatus(null);
        setMessage("");
        setEditMode(false);
      }
      return null;
    }

    if (!prefetch && setCurrent) {
      setStatus("loading");
      setMessage(strings.statusExtracting);
    }

    try {
      const formData = new FormData();
      formData.append("blNumber", "AUTO");
      formData.append("invoiceNumber", "AUTO");
      formData.append("file", file);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5 * 60 * 1000);

      const token = await getToken();
      const response = await fetch(API_ENDPOINTS.proofOfPayment, {
        method: "POST",
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error("Failed to submit Proof of Payment");
      }

      const data = await response.json();
      const result = data && (data.data || data);
      
      // Check if the result contains an error from Azure
      if (result && result.error) {
        throw new Error(result.error);
      }
      
      setToolResultsByFile((prev) => ({ ...prev, [fileKey]: result }));
      if (setCurrent) {
        setToolResult(result);
        setEditedResult("");
      }
      setEditedResultsByFile((prev) => {
        const updated = { ...prev };
        delete updated[fileKey];
        return updated;
      });
      if (setCurrent) {
        setStatus(null);
        setMessage("");
      }
      return result;
    } catch (error) {
      console.error(error);
      if (setCurrent) {
        setStatus("error");
        const isAbort = error.name === "AbortError";
        const isNetwork = !error.message || error.message === "Failed to fetch" || String(error.message).includes("EMPTY_RESPONSE") || String(error.message).includes("Load failed");
        const errorMsg = isAbort
          ? "Request took too long. Try again or use a smaller file."
          : isNetwork
            ? "Connection lost or server did not respond. Extraction may be timing out—ask your admin to increase proxy/server timeouts for /api/proof-of-payment (e.g. 120–180 seconds)."
            : (error.message || strings.extractError);
        setMessage(errorMsg);
      }
    }
  };

  const handleFilesUploaded = (newFiles) => {
    if (!newFiles || newFiles.length === 0) return;
    // Merge new files into existing uploadedFiles (don't replace)
    setUploadedFiles((prev) => {
      const existingKeys = new Set(prev.map(getFileKey));
      const toAdd = newFiles.filter((f) => !existingKeys.has(getFileKey(f)));
      return [...prev, ...toAdd];
    });
    // Don't auto-extract — user must click Extract
    setCurrentFileName((prev) => prev || newFiles[0]?.name || "");
    setCurrentFileKey((prev) => prev || getFileKey(newFiles[0]) || "");
    setToolResult((prev) => (prev !== null ? prev : null));
    setEditedResult((prev) => prev || "");
    setEditMode(false);
  };

  const callConfirmApi = async (fileData, metadataOverride = null, skipVerify = false) => {
    // fileData: single { fileKey, fileName, blNumber, invoiceNumber, country, type }
    if (!fileData) return;
    const { fileKey, fileName, blNumber, invoiceNumber, country, type } = fileData;
    const original = metadataOverride || toolResultsByFile[fileKey] || {};
    const edits = metadataOverride ? {} : (editedResultsByFile[fileKey] || {});
    const finalMetadata = { ...original, ...edits };
    const changedOnly = {};
    Object.entries(edits).forEach(([key, val]) => {
      const orig = original[key] == null ? "" : String(original[key]);
      const edited = val == null ? "" : String(val);
      if (orig !== edited) changedOnly[key] = val;
    });
    const changedValues = Object.keys(changedOnly).length > 0 ? changedOnly : null;
    const filePayload = {
      document_id: `BL-${blNumber || "AUTO"}-INV-${invoiceNumber || "AUTO"}`,
      file_name: fileName,
      bl_number: blNumber || "",
      invoice_number: invoiceNumber || "",
      metadata: finalMetadata,
      changed_values: changedValues,
      type: type || null,
    };
    try {
      const token = await getToken();
      const res = await fetch(API_ENDPOINTS.confirmExtraction, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ files: [filePayload] }),
      });
      const data = await res.json();
      if (data.case_id) {
        setCaseIdsByFile((prev) => ({ ...prev, [fileKey]: data.case_id }));
        setSubmissionHistory((prev) => [
          {
            caseId: data.case_id,
            submittedAt: new Date(),
            files: [{
              fileName: filePayload.file_name,
              blNumber: filePayload.bl_number,
              invoiceNumber: filePayload.invoice_number,
              type: filePayload.type || "",
              metadata: filePayload.metadata || {},
              changed_values: filePayload.changed_values || null,
            }],
            bl_number: filePayload.bl_number,
            invoice_number: filePayload.invoice_number,
            metadata: filePayload.metadata,
            country_code: country || "",
          },
          ...prev,
        ]);

        if (VERIFY_ON_CONFIRM && !skipVerify) {
          setVerifyingByFile((prev) => ({ ...prev, [fileKey]: true }));
          try {
            const verifyRes = await fetch(API_ENDPOINTS.verifyCase, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({
                case_id: data.case_id,
                bl_number: filePayload.bl_number,
                invoice_number: filePayload.invoice_number,
                extracted_metadata: filePayload.metadata || {},
                country_code: country || "",
              }),
            });
            const verifyData = await verifyRes.json();
            setVerificationResults((prev) => ({ ...prev, [data.case_id]: verifyData }));
            if (verifyData.resolved_bl && !filePayload.bl_number) {
              setSubmissionHistory((prev) =>
                prev.map((e) => e.caseId === data.case_id ? { ...e, bl_number: verifyData.resolved_bl } : e)
              );
            }
          } catch (verifyErr) {
            console.error("Verify API error:", verifyErr);
          } finally {
            setVerifyingByFile((prev) => ({ ...prev, [fileKey]: false }));
          }
        }
      }
      return data.case_id || null;
    } catch (err) {
      console.error("Confirm API error:", err);
    }
    return null;
  };

  const handleSubmit = async (payload, action) => {
    if (action === "confirm") {
      const allFilesData = payload.allFilesData || [payload.currentFileData].filter(Boolean);
      setIsConfirming(true);
      await Promise.all(allFilesData.map((fd) => callConfirmApi(fd, null, true)));
      setIsConfirming(false);
      setEditMode(false);
    } else if (action === "proceedExtract") {
      const targetFile =
        payload?.file ||
        uploadedFiles.find((f) => getFileKey(f) === currentFileKey) ||
        uploadedFiles[0];
      if (!targetFile) return;
      const fileKey = getFileKey(targetFile);

      const result = await handleFileUploaded(targetFile, { prefetch: false, setCurrent: true, extractIfMissing: true });

      if (result && !result.error) {
        setEditMode(false);

        // If only BL was provided (no invoice), fire a quick background lookup from S4 table
        if (payload.blNumber && !payload.invoiceNumber) {
          (async () => {
            try {
              const token = await getToken();
              const url = `${API_ENDPOINTS.s4InvoiceLookup}?bl_number=${encodeURIComponent(payload.blNumber)}`;
              const res = await fetch(url, {
                headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
              });
              const data = await res.json();
              if (data.invoice_number) {
                setS4InvoiceByFile(prev => ({ ...prev, [fileKey]: data.invoice_number }));
              }
            } catch { /* silent — non-blocking */ }
          })();
        }
      }
    } else if (action === "validate") {
      const fileKey = payload?.fileKey || currentFileKey;

      // Step 1: DB insertion
      const fileData = {
        fileKey,
        fileName: payload.fileName || uploadedFiles.find(f => getFileKey(f) === fileKey)?.name || "",
        blNumber: payload.blNumber || "",
        invoiceNumber: payload.invoiceNumber || "",
        country: payload.country || "",
        type: payload.type || "",
      };
      setIsConfirming(true);
      const caseIdResult = await callConfirmApi(fileData, null, true);
      setIsConfirming(false);
      if (!caseIdResult) return;

      // Step 2: verifyCase with real case_id for validation results
      const token = await getToken();
      setVerifyingByFile((prev) => ({ ...prev, [fileKey]: true }));
      try {
        const verifyRes = await fetch(API_ENDPOINTS.verifyCase, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            case_id: caseIdResult,
            bl_number: payload.blNumber || "",
            invoice_number: payload.invoiceNumber || "",
            extracted_metadata: toolResultsByFile[fileKey] || {},
            country_code: payload.country || "",
          }),
        });
        const verifyData = await verifyRes.json();
        setVerificationResults((prev) => ({ ...prev, [caseIdResult]: verifyData }));
        // Populate "Invoice from S4" field from this single verifyCase response
        const s4Invoice =
          verifyData.invoice_number ||
          verifyData.s4_invoice_number ||
          (verifyData.rules || []).find(r => r.rule_name === "INVOICE_VALIDATION")?.booking_data ||
          "";
        if (s4Invoice) {
          setS4InvoiceByFile((prev) => ({ ...prev, [fileKey]: s4Invoice }));
        }
        if (verifyData.resolved_bl && !payload.blNumber) {
          setSubmissionHistory((prev) =>
            prev.map((e) => e.caseId === caseIdResult ? { ...e, bl_number: verifyData.resolved_bl } : e)
          );
        }
      } catch (err) {
        console.error("Validate error:", err);
      } finally {
        setVerifyingByFile((prev) => ({ ...prev, [fileKey]: false }));
      }
    } else if (action === "edit") {
      setStatus(null);
      const targetFile =
        payload?.file ||
        uploadedFiles.find((f) => getFileKey(f) === currentFileKey) ||
        uploadedFiles[0];

      if (targetFile) {
        const fileKey = getFileKey(targetFile);
        let result = toolResultsByFile[fileKey];
        if (result === undefined || result === null) {
          result = await handleFileUploaded(targetFile, {
            prefetch: false,
            setCurrent: false,
            extractIfMissing: true,
          });
        }
        setCurrentFileName(targetFile.name || "");
        setCurrentFileKey(fileKey);
        setToolResult(result ?? null);
        setEditedResult(editedResultsByFile[fileKey] ?? "");
      }
      setShowEditFilePicker(false);
      setEditMode(true);
    } else if (action === "save") {
      // Keep toolResultsByFile as the original extracted values (used by
      // getChangesSummary to detect diffs). Display is handled via
      // viewToolResult (see render) which overlays editedResultsByFile.
      setStatus(null);
      setMessage("");
      setEditMode(false);
    } else {
      setStatus(null);
    }
  };

  const handleFileSelectedForEdit = async (file) => {
    setShowEditFilePicker(false);
    const fileKey = getFileKey(file);
    let result = toolResultsByFile[fileKey];
    if (result === undefined || result === null) {
      result = await handleFileUploaded(file, {
        prefetch: false,
        setCurrent: false,
        extractIfMissing: true,
      });
    }
    setCurrentFileName(file.name || "");
    setCurrentFileKey(fileKey);
    setToolResult(result ?? null);
    setEditedResult(editedResultsByFile[fileKey] ?? "");
    setEditMode(true);
  };

  const handleContinue = () => {
    setStatus("success");
  };

  const handleGoToDashboard = () => {
    setStatus(null);
    setMessage("");
    setEditMode(false);
    setShowMismatchPrompt(false);
    setCurrentScreen("dashboard");
  };

  const handleReset = () => {
    setStatus(null);
    setMessage("");
    setToolResult(null);
    setToolResultsByFile({});
    setEditedResultsByFile({});
    setMismatchEmail("");
    setEditMode(false);
    setEditedResult("");
    setCurrentFileName("");
    setCurrentFileKey("");
    setShowMismatchPrompt(false);
    setMismatchComment("");
    setUploadedFiles([]);
    setShowEditFilePicker(false);
    setCaseIdsByFile({});
    setVerifyingByFile({});
    setS4LookupByFile({});
    setS4InvoiceByFile({});
  };

  const handleFileRemoved = (file) => {
    if (!file) return;
    const fileKey = getFileKey(file);
    setUploadedFiles((prev) => prev.filter((f) => getFileKey(f) !== fileKey));
    setToolResultsByFile((prev) => { const u = { ...prev }; delete u[fileKey]; return u; });
    setEditedResultsByFile((prev) => { const u = { ...prev }; delete u[fileKey]; return u; });
    setCaseIdsByFile((prev) => { const u = { ...prev }; delete u[fileKey]; return u; });
    setVerifyingByFile((prev) => { const u = { ...prev }; delete u[fileKey]; return u; });
    if (currentFileKey === fileKey) {
      setToolResult(null);
      setEditedResult("");
      setEditMode(false);
      setStatus(null);
      setMessage("");
      setCurrentFileName("");
      setCurrentFileKey("");
    }
  };

  const handleClearToolResult = () => {
    setToolResult(null);
    setEditedResult("");
    setEditMode(false);
    setStatus(null);
    setMessage("");
    setCurrentFileName("");
    setCurrentFileKey("");
  };

  const handleMismatchContinue = async () => {
    setIsConfirming(true);
    const pending = Array.isArray(pendingConfirmData) ? pendingConfirmData : [pendingConfirmData].filter(Boolean);
    await Promise.all(pending.map((fd) => callConfirmApi(fd)));
    setIsConfirming(false);
    setPendingConfirmData([]);
    setShowMismatchPrompt(false);
    setEditMode(false);
  };

  const handleMismatchEdit = () => {
    setShowMismatchPrompt(false);
    setEditMode(true);
    setMismatchComment("");
    setMismatchEmail("");
  };

  const handleManualVerify = async (entry) => {
    const token = await getToken();
    try {
      const verifyRes = await fetch(API_ENDPOINTS.verifyCase, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          case_id: entry.caseId,
          bl_number: entry.bl_number || "",
          invoice_number: entry.invoice_number || "",
          extracted_metadata: entry.metadata || {},
          country_code: entry.country_code || "",
        }),
      });
      const verifyData = await verifyRes.json();
      setVerificationResults((prev) => ({ ...prev, [entry.caseId]: verifyData }));
      // Write resolved BL back so View Details can query shipments/tasks by BL
      if (verifyData.resolved_bl && !entry.bl_number) {
        setSubmissionHistory((prev) =>
          prev.map((e) => e.caseId === entry.caseId ? { ...e, bl_number: verifyData.resolved_bl } : e)
        );
      }
    } catch (err) {
      console.error("Manual verify error:", err);
    }
  };

  const getChangesSummary = () => {
    const allChanges = [];
    uploadedFiles.forEach((file) => {
      const fileKey = getFileKey(file);
      const originalResult = toolResultsByFile[fileKey];
      if (!originalResult || typeof originalResult !== "object") return;
      const editedResult = editedResultsByFile[fileKey];
      const allFields = [];
      Object.entries(originalResult).filter(([k]) => !k.startsWith("_")).forEach(([key, originalValue]) => {
        const finalValue =
          editedResult && editedResult[key] !== undefined
            ? editedResult[key]
            : originalValue;
        const isChanged =
          editedResult !== undefined &&
          normalizeValue(originalValue) !== normalizeValue(finalValue);
        allFields.push({
          field: key,
          original: originalValue,
          edited: finalValue,
          isChanged,
        });
      });
      if (allFields.length > 0) {
        allChanges.push({ fileName: file.name, fileKey, allFields });
      }
    });
    return allChanges;
  };

  // For readonly display: overlay saved edits on top of original so the
  // tool result view shows the user's corrections after clicking Save,
  // while toolResultsByFile retains originals for change-summary diffing.
  const caseId = caseIdsByFile[currentFileKey] || "";
  const isVerifying = !!verifyingByFile[currentFileKey];

  // Derive per-file verification results (fileKey → verificationResult)
  const verificationResultsByFile = Object.fromEntries(
    Object.entries(caseIdsByFile)
      .filter(([, cId]) => verificationResults[cId])
      .map(([fileKey, cId]) => [fileKey, verificationResults[cId]])
  );

  const viewToolResult = (() => {
    const edits = editedResultsByFile[currentFileKey];
    if (
      !editMode &&
      edits &&
      toolResult &&
      typeof toolResult === "object" &&
      !Array.isArray(toolResult) &&
      typeof edits === "object"
    ) {
      return { ...toolResult, ...edits };
    }
    return toolResult;
  })();

  return (
    <>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
      {currentScreen === "dashboard" ? (
        <DashboardScreen
          userName={account?.name || account?.username || ""}
          userPhotoUrl={userPhotoUrl}
          submissionHistory={submissionHistory}
          onNewSubmission={() => {
            setCurrentScreen("form");
            handleReset();
          }}
          language={language}
          onLanguageChange={setLanguage}
          strings={strings}
          verifyOnConfirm={VERIFY_ON_CONFIRM}
          onVerify={handleManualVerify}
          verificationResults={verificationResults}
          getToken={getToken}
        />
      ) : (
        <>
        <AFRPopSubmissionForm
          onSubmit={handleSubmit}
          status={status}
          onReset={handleReset}
          onGoToDashboard={handleGoToDashboard}
          toolResult={toolResult}
          viewToolResult={viewToolResult}
          editMode={editMode}
          editedResult={editedResult}
          onEditedResultChange={(newEdited) => {
            setEditedResult(newEdited);
            if (currentFileKey) {
              setEditedResultsByFile((prev) => ({
                ...prev,
                [currentFileKey]: newEdited,
              }));
            }
          }}
          editedToolResult={editedResultsByFile[currentFileKey] ?? null}
          onFileUploaded={handleFileUploaded}
          onFilesUploaded={handleFilesUploaded}
          onFileRemoved={handleFileRemoved}
          onClearToolResult={handleClearToolResult}
          strings={strings}
          language={language}
          onLanguageChange={setLanguage}
          userName={account?.name || account?.username || ""}
          userPhotoUrl={userPhotoUrl}
          onLogout={handleLogout}
          changesSummary={getChangesSummary()}
          showMismatchPanel={showMismatchPrompt}
          mismatchEmail={mismatchEmail}
          onMismatchEmailChange={setMismatchEmail}
          mismatchComment={mismatchComment}
          onMismatchCommentChange={setMismatchComment}
          onMismatchEdit={handleMismatchEdit}
          onMismatchContinue={handleMismatchContinue}
          isConfirming={isConfirming}
          isVerifying={isVerifying}
          caseId={caseId}
          onContinue={handleContinue}
          showEditFilePicker={showEditFilePicker}
          onEditFilePickerClose={() => setShowEditFilePicker(false)}
          uploadedFiles={uploadedFiles}
          onFileSelectedForEdit={handleFileSelectedForEdit}
          verificationResult={verificationResults[caseId] || null}
          s4Lookup={s4LookupByFile[currentFileKey] || null}
          s4InvoiceNumber={s4InvoiceByFile[currentFileKey] || ""}
          caseIdsByFile={caseIdsByFile}
          verifyingByFile={verifyingByFile}
          verificationResultsByFile={verificationResultsByFile}
          getToken={getToken}
          userEmail={account?.username || ""}
          onCancelEdit={() => {
            setEditMode(false);
            setEditedResult("");
            if (currentFileKey) {
              setEditedResultsByFile((prev) => {
                const updated = { ...prev };
                delete updated[currentFileKey];
                return updated;
              });
            }
          }}
          onSubmitForManualReview={() => setShowMismatchPrompt(true)}
        />
        {status && status !== "success" && status !== "loading" && (
          <div
            style={{
              position: "fixed",
              bottom: 20,
              right: 20,
              padding: "10px 16px",
              borderRadius: 999,
              background:
                status === "success"
                  ? "#02888f"
                  : status === "error"
                    ? "#d32f2f"
                    : "#607d8b",
              color: "#fff",
              fontSize: "0.85rem",
              boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
            }}
          >
            {message}
          </div>
        )}
        </>
      )}
      </AuthenticatedTemplate>
    </>
  );
}

export default App;
